import React, { useCallback, Fragment, useMemo } from 'react';

import { useQueryLoader } from 'react-relay';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/migration/show-more-horizontal--more';
import { SkeletonItem } from '@atlaskit/menu';
import { Box, xcss } from '@atlaskit/primitives';

import Placeholder from '@atlaskit/react-ufo/placeholder';
import FieldIcon from '@atlaskit/icon/core/field';
import SearchIcon from '@atlaskit/icon/core/search';
import { JiraIssueCreateErrorBoundary } from '@atlassian/jira-issue-create-analytics-common/src/ui/jira-issue-create-error-boundary/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import type { FieldConfig } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { transformProjectType } from '@atlassian/jira-issue-create-commons/src/common/utils/transform-project-type/index.tsx';
import { useFormContext } from '@atlassian/jira-issue-create-form-context/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import smartfieldsDropdownItemAdminToggleQuery, {
	type smartfieldsDropdownItemAdminToggleQuery as SmartFieldsAdminToggleQuery,
} from '@atlassian/jira-relay/src/__generated__/smartfieldsDropdownItemAdminToggleQuery.graphql.ts';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import type { WatchersConfig } from '@atlassian/jira-issue-create-watchers/src/services/types.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import Watchers from '../watchers/index.tsx';
import messages from './messages.tsx';
import { SmartFieldsDropdownItem } from './smartfields-dropdown-item/index.tsx';

type Props = {
	isDisabled?: boolean;
	fieldConfig?: FieldConfig;
	watchersConfigurations?: WatchersConfig;
};

const testId = 'issue-create.ui.modal.create-form.actions-container.dropdown-menu.dropdown-menu';
const buttonTestId = 'issue-create.ui.modal.create-form.actions-container.dropdown-menu.button';

// TODO: Integrate placeholder back into the dropdown component when cleaning up smart_fields_gic_toggle_fg
const LoadingPlaceholder = ({ children }: { children: React.ReactNode }) => (
	<Placeholder
		name="header-dropdown-smart-fields-gic-toggle"
		fallback={
			<Box xcss={loadingPlaceholderStyles}>
				<SkeletonItem width="100%" isShimmering />
				<SkeletonItem width="100%" isShimmering />
				<SkeletonItem width="100%" isShimmering />
			</Box>
		}
	>
		{children}
	</Placeholder>
);

export const HeaderDropdown = (props: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const isAdmin = useIsAdmin();
	const cloudId = useCloudId();
	const { isDisabled = false, watchersConfigurations } = props;

	const [
		{ selectedProject, selectedIssueType },
		{ toggleConfigureFieldsModalVisibility, toggleFindFieldsModalVisibility },
	] = useFormContext();

	const [queryReference, loadQuery] = useQueryLoader<SmartFieldsAdminToggleQuery>(
		smartfieldsDropdownItemAdminToggleQuery,
	);

	const showConfigureFieldsModal = (): void => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		toggleConfigureFieldsModalVisibility(true);
		fireUIAnalytics(analyticsEvent, 'configureFields', {
			projectType: selectedProject ? transformProjectType(selectedProject.projectType) : 'none',
		});
	};

	const showFindFieldModal = (): void => {
		const analyticsEvent = createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
		});
		toggleFindFieldsModalVisibility(true);
		fireUIAnalytics(analyticsEvent, 'findYourField', {
			projectType: selectedProject ? transformProjectType(selectedProject.projectType) : 'none',
		});
	};

	const showFindFieldOption = (): boolean =>
		Boolean(isAdmin && selectedProject && selectedIssueType && !selectedProject.isSimplified);

	const loadSmartFieldsAdminToggleQuery = useCallback(() => {
		loadQuery({
			cloudId,
		});
	}, [cloudId, loadQuery]);

	// @ts-expect-error - TS7006 - Parameter 'attributes' implicitly has an 'any' type.
	const onOpenChange = (attributes) => {
		if (attributes.isOpen) {
			const analyticsEvent = createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
			});
			fireUIAnalytics(analyticsEvent, 'meatballMenu');

			if (fg('smart_fields_gic_toggle_fg')) {
				loadSmartFieldsAdminToggleQuery();
			}
		}
	};
	const disableDropdown =
		isVisualRefreshEnabled() && fg('visual-refresh_drop_6')
			? isDisabled &&
				!watchersConfigurations?.canManageWatchers &&
				!watchersConfigurations?.isWatchersEnabled
			: isDisabled;

	const shouldShowDropdownActions =
		isVisualRefreshEnabled() && fg('visual-refresh_drop_6') ? !isDisabled : true;

	const Wrapper = fg('smart_fields_gic_toggle_fg') ? LoadingPlaceholder : Fragment;

	const fieldIcon =
		isVisualRefreshEnabled() && fg('visual-refresh_drop_6') ? <FieldIcon label="" /> : null;
	const findFieldsIcon =
		isVisualRefreshEnabled() && fg('visual-refresh_drop_6') ? <SearchIcon label="" /> : null;
	const attributes = useMemo(
		() => ({
			shouldShowDropdownActions,
			disableDropdown,
			isDisabled,
			isAdmin,
		}),
		[disableDropdown, isAdmin, isDisabled, shouldShowDropdownActions],
	);
	return (
		<DropdownMenu
			placement="bottom-end"
			shouldRenderToParent
			trigger={({ triggerRef, ...dropdownProps }) => (
				<Button
					{...dropdownProps}
					testId={buttonTestId}
					iconBefore={<MoreIcon spacing="spacious" label="" />}
					appearance="subtle"
					isDisabled={disableDropdown}
					ref={triggerRef}
					aria-label={formatMessage(
						expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
							? messages.moreActionsIssueTermRefresh
							: messages.moreActions,
					)}
				/>
			)}
			onOpenChange={onOpenChange}
			testId={testId}
		>
			<DropdownItemGroup>
				<Wrapper>
					{shouldShowDropdownActions && (
						<>
							<DropdownItem elemBefore={fieldIcon} onClick={showConfigureFieldsModal}>
								{fg('smart_fields_gic_toggle_fg')
									? formatMessage(messages.configureFields)
									: formatMessage(messages.showFields)}
							</DropdownItem>
							{queryReference && fg('smart_fields_gic_toggle_fg') ? (
								<JiraIssueCreateErrorBoundary
									id="smartFieldsGicToggleDropdownItem"
									packageName="jiraIssueCreate"
									fallback="unmount"
									sendToPrivacyUnsafeSplunk
									{...(fg('jira-issue-create-error-boundary-enabled') ? { attributes } : {})}
								>
									<SmartFieldsDropdownItem queryReference={queryReference} />
								</JiraIssueCreateErrorBoundary>
							) : null}
							{showFindFieldOption() && (
								<DropdownItem elemBefore={findFieldsIcon} onClick={showFindFieldModal}>
									{formatMessage(messages.findField)}
								</DropdownItem>
							)}
						</>
					)}
					{isVisualRefreshEnabled() && fg('visual-refresh_drop_6') && (
						<Watchers configs={watchersConfigurations} shouldRenderInDropdown />
					)}
				</Wrapper>
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

const loadingPlaceholderStyles = xcss({
	width: '204px',
});
